import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { format } from 'date-fns';
import type { TimeEntry } from '../../../core/models/TimeEntry';
import type { TimeEntryExportContext } from './timeEntryExportTypes';

const formatHours = (minutes: number) => (minutes / 60).toFixed(2);

const totalMinutes = (entries: TimeEntry[]) =>
  entries.reduce((sum, entry) => sum + entry.duration, 0);

const billableMinutes = (entries: TimeEntry[]) =>
  entries.filter((entry) => entry.billable).reduce((sum, entry) => sum + entry.duration, 0);

const styles = StyleSheet.create({
  page: {
    padding: 36,
    fontSize: 9,
    color: '#1f2937',
    fontFamily: 'Helvetica',
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
    paddingBottom: 14,
    marginBottom: 18,
  },
  brand: {
    fontSize: 11,
    color: '#003049',
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: '#111827',
  },
  meta: {
    marginTop: 10,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  metaItem: {
    width: '48%',
    marginBottom: 4,
  },
  label: {
    color: '#6b7280',
    fontSize: 8,
    marginBottom: 2,
  },
  value: {
    fontSize: 10,
    color: '#111827',
  },
  summary: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    marginBottom: 16,
  },
  summaryBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 10,
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: 700,
    color: '#003049',
  },
  table: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    minHeight: 24,
  },
  headerRow: {
    backgroundColor: '#f3f4f6',
  },
  cell: {
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: '#e5e7eb',
  },
  dateCell: { width: '14%' },
  projectCell: { width: '16%' },
  personCell: { width: '15%' },
  descriptionCell: { width: '31%' },
  hoursCell: { width: '8%' },
  statusCell: { width: '10%' },
  billableCell: { width: '6%', borderRightWidth: 0 },
  th: {
    fontWeight: 700,
    color: '#374151',
  },
  footer: {
    position: 'absolute',
    bottom: 18,
    left: 36,
    right: 36,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: '#6b7280',
    fontSize: 8,
  },
});

export const TimeEntryPdfDocument: React.FC<{
  entries: TimeEntry[];
  context: TimeEntryExportContext;
}> = ({ entries, context }) => {
  const generatedAt = context.generatedAt ?? new Date();

  return (
    <Document title="Time Records">
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.brand}>Sentral Group</Text>
          <Text style={styles.title}>Time Records</Text>
          <View style={styles.meta}>
            <View style={styles.metaItem}>
              <Text style={styles.label}>Client</Text>
              <Text style={styles.value}>{context.clientName || 'All clients'}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.label}>Project</Text>
              <Text style={styles.value}>{context.projectName || 'All projects'}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.label}>User</Text>
              <Text style={styles.value}>{context.userName || 'All users'}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.label}>Date range</Text>
              <Text style={styles.value}>{context.dateRangeLabel}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.label}>Generated</Text>
              <Text style={styles.value}>{format(generatedAt, 'PPp')}</Text>
            </View>
          </View>
        </View>

        <View style={styles.summary}>
          <View style={styles.summaryBox}>
            <Text style={styles.label}>Records</Text>
            <Text style={styles.summaryValue}>{entries.length}</Text>
          </View>
          <View style={styles.summaryBox}>
            <Text style={styles.label}>Total hours</Text>
            <Text style={styles.summaryValue}>{formatHours(totalMinutes(entries))}</Text>
          </View>
          <View style={styles.summaryBox}>
            <Text style={styles.label}>Billable hours</Text>
            <Text style={styles.summaryValue}>{formatHours(billableMinutes(entries))}</Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={[styles.row, styles.headerRow]} fixed>
            <Text style={[styles.cell, styles.dateCell, styles.th]}>Date</Text>
            <Text style={[styles.cell, styles.projectCell, styles.th]}>Project</Text>
            <Text style={[styles.cell, styles.personCell, styles.th]}>User</Text>
            <Text style={[styles.cell, styles.descriptionCell, styles.th]}>Description</Text>
            <Text style={[styles.cell, styles.hoursCell, styles.th]}>Hours</Text>
            <Text style={[styles.cell, styles.statusCell, styles.th]}>Status</Text>
            <Text style={[styles.cell, styles.billableCell, styles.th]}>Bill.</Text>
          </View>
          {entries.map((entry) => (
            <View key={entry.id} style={styles.row} wrap={false}>
              <Text style={[styles.cell, styles.dateCell]}>{format(entry.startTime, 'MMM d, yyyy')}</Text>
              <Text style={[styles.cell, styles.projectCell]}>
                {entry.projectId
                  ? context.projectNamesById?.[entry.projectId] || entry.projectId
                  : 'Client only'}
              </Text>
              <Text style={[styles.cell, styles.personCell]}>
                {context.userNamesById?.[entry.userId] || entry.userId}
              </Text>
              <Text style={[styles.cell, styles.descriptionCell]}>{entry.description}</Text>
              <Text style={[styles.cell, styles.hoursCell]}>{formatHours(entry.duration)}</Text>
              <Text style={[styles.cell, styles.statusCell]}>{entry.status}</Text>
              <Text style={[styles.cell, styles.billableCell]}>{entry.billable ? 'Yes' : 'No'}</Text>
            </View>
          ))}
        </View>

        <View style={styles.footer} fixed>
          <Text>Times are exported from filtered time tracking records.</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};
